import { CSSObject } from '@emotion/css'
import styled from '@emotion/styled'
import { createPropFilter, WebLink, WebLinkProps } from '@news-mono/web-common'
import { playButtonStates } from '../../buttons/PlayButton/PlayButton.styled'
import {
    styledGalleryButtonClassName,
    styledMediaClassName,
    styledPlayButtonClassName,
} from '../../cards/CardMedia/StyledMedia.styled'
import { StyledHeadlineText } from '../../cards/CardText/CardText.styled'
import { styledCardTimestampTextLabel } from '../../content/CardTimestamp/CardTimestamp.styled'
import { headlineBoxshadow } from '../../__styling/mixins/headline-boxshadow'
import { colors } from '../../__styling/settings/colors'
import { transition } from '../../__styling/style-mixins'
import { themedValue } from '../../__styling/themed-value'
import { StyledKicker } from '../Kicker/Kicker.styled'
import { styledWrapperClassName } from '../LandscapeWide/Styled/StyledText.styled'

export interface StyledPortraitCardProps {
    isSponsored: boolean | null
    hasBackground?: boolean
    primaryTopic: string | undefined
    hasPadding?: boolean
}

// Interaction Styles for Cards on PerthNow
export const cardInteractionFocusStylesPerthNow: CSSObject = {
    '&:focus': {
        outline: 'none',

        [`& ${StyledHeadlineText}`]: {
            textDecoration: 'underline',
        },

        [`& .${styledMediaClassName}, & + .${styledWrapperClassName} .${styledMediaClassName}`]:
            {
                '&::after': {
                    borderColor: colors.perthnow.greyCoal,
                },
            },
    },
}

export const CardInteractionHoverStylesPerthNow: CSSObject = {
    '&:hover': {
        [`& ${StyledHeadlineText}`]: {
            color: colors.perthnow.blueKyanite,
        },

        [`& .${styledMediaClassName}`]: {
            '&::after': {
                height: '100%',
                borderColor: colors.white50,
            },
        },
    },
}

export const CardInteractionPlayButtonStylesPerthNow: CSSObject = {
    '&:hover, &:focus': {
        [`& .${styledPlayButtonClassName}`]: {
            backgroundColor: colors.perthnow.pinkThulite,

            '&::before': {
                borderLeftColor: colors.white, // Play button CSS color, uses border to create the triangle
            },
        },
    },
}

export interface StyledLinkProps {
    hasBackground?: boolean
    isLoading?: boolean
    isLarge?: boolean
}

export const StyledLink = styled(WebLink, {
    shouldForwardProp: createPropFilter<WebLinkProps>()([
        'hasBackground',
        'isLoading',
        'isLarge',
        'section',
    ]),
})<StyledLinkProps>(
    {
        textDecoration: 'none',
        textDecorationSkipInk: 'auto',
        ...transition({ properties: ['color'] }),
    },
    ({ theme }) => ({
        color: themedValue(theme, {
            fallback: colors.black,
            perthnow: colors.perthnow.greyCoal,
        }),
        '&:hover, &:focus': {
            [`& .${styledGalleryButtonClassName}`]: {
                backgroundColor: themedValue(theme, {
                    thewest: (section) => section.primaryColor,
                    perthnow: colors.perthnow.pinkThulite,
                    fallback: colors.black,
                }),
            },
        },
    }),
    ({ isLoading }) =>
        isLoading && {
            cursor: 'default',
        },

    // Interactions on Cards - PerthNow
    ({ theme, hasBackground }) =>
        theme.kind === 'perthnow' && [
            cardInteractionFocusStylesPerthNow,
            !hasBackground && CardInteractionHoverStylesPerthNow,
            CardInteractionPlayButtonStylesPerthNow,
        ],

    // PerthNow only for cards with a background colour
    ({ theme, hasBackground }) =>
        theme.kind === 'perthnow' &&
        hasBackground && {
            color: colors.white,

            '&:hover': {
                [`& ${StyledHeadlineText}`]: {
                    ...headlineBoxshadow,
                    color: colors.white,
                    background: colors.perthnow.blueSapphire,
                },
            },
        },

    // Interactions on Card Image - The West
    ({ theme }) =>
        theme.kind === 'thewest' && {
            [`& .${styledMediaClassName}`]: {
                '&::after': {
                    height: '100%',
                    borderWidth: '0 0 4px 0',
                    borderColor: 'transparent',
                },
            },

            '&:focus': {
                outline: 'none',

                [`& ${StyledHeadlineText}`]: {
                    textDecoration: 'underline',
                },

                [`& .${styledMediaClassName}`]: {
                    '&::after': {
                        borderColor: colors.thewest.greyGorilla,
                    },
                },

                [`& ${StyledKicker}`]: {
                    textDecoration: 'underline',
                    color: colors.black,
                },
            },

            '&:hover': {
                [`& ${StyledHeadlineText}`]: {
                    color: theme.sectionValues.primaryColor,
                },

                [`& .${styledMediaClassName}`]: {
                    '&::after': {
                        borderColor: theme.sectionValues.primaryColor,
                    },
                },

                [`& ${StyledKicker} svg`]: {
                    fill: theme.sectionValues.primaryColor,
                },
            },
        },

    // Interactions on Card Image - Sevennews
    ({ theme, hasBackground }) =>
        theme.kind === 'sevennews' && {
            ...playButtonStates(theme),

            [`& .${styledMediaClassName}`]: {
                '&::after': {
                    borderWidth: '0 0 4px 0',
                    borderColor: 'transparent',
                },
            },

            '&:hover': {
                [`& .${styledMediaClassName}`]: {
                    '&::before': {
                        height: '120%',
                    },
                },
                [`& ${StyledHeadlineText}`]: {
                    color: themedValue(theme, {
                        sevennews: (section) =>
                            hasBackground
                                ? undefined
                                : section.cards.headline.hoverColor,
                        fallback: undefined,
                    }),
                    textDecoration: 'underline',
                },
                [`& .${styledCardTimestampTextLabel}`]: {
                    color: themedValue(theme, {
                        sevennews: (section) =>
                            section.cards.timestamp.hoverColor,
                        fallback: undefined,
                    }),
                },
            },

            '&:focus': {
                outline: `solid 2px`,
                outlineColor: themedValue(theme, {
                    sevennews: (section) => section.focusColor,
                    fallback: undefined,
                }),
            },

            // to prevent flash on desktop when clicking through
            '&:active': {
                outline: themedValue(theme, {
                    sevennews: 'none',
                    fallback: undefined,
                }),
            },
        },
)
